import { Box, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { kioskClockOutRequest } from 'api/requests/kiosk-session-requests';
import UPButton from 'components/button';
import UPDatepicker from 'components/datepicker';
import UPInput from 'components/input';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import kioskSessionService from 'services/kiosk-session-service';
import {
  GENERAL__ERROR_MESSAGE,
  KIOSK__COST_CENTRE_LABEL,
  KIOSK__END_REGISTRATION_BUTTON,
  KIOSK__END_REGISTRATION_DEVIATION_LABEL,
  KIOSK__END_REGISTRATION_TITLE,
  KIOSK__END_REGISTRATION_TOTAL_WORKED_LABEL,
  KIOSK__START_REGISTRATION_DATE_LABEL,
  KIOSK__START_REGISTRATION_END_LABEL,
  KIOSK__START_REGISTRATION_POLICIES_INTRO,
  KIOSK__START_REGISTRATION_POLICIES_TITLE,
  KIOSK__START_REGISTRATION_START_LABEL,
} from 'translations/constants';
import { TKioskBreak } from 'types/kiosk/TKioskClockOutData';
import { formatTime, parseUtcTimestamp } from 'utils/date-utils';
import {
  areBreaksValid,
  buildKioskClockOutPayload,
  computeWorkSummary,
  extractWorkedMinutes,
  formatDeviation,
  formatWorkedDuration,
  getOverlappingIndices,
  isHHmm,
} from 'utils/kiosk-clock-out-logic';
import { currentTimeHHmm, kioskDateBounds } from 'utils/kiosk-date';
import { mapKioskError } from 'utils/kiosk-error';
import KioskBreakEditor from './components/KioskBreakEditor';
import { formatKioskTimeInput } from './kiosk-time-input';
import { useEndKioskSession } from './useEndKioskSession';
import { useKioskClockOutData } from './useKioskClockOutData';
import {
  KioskErrorMessage,
  KioskFieldContainer,
  KioskFieldRow,
  KioskPoliciesAccordion,
  KioskPoliciesDetails,
  KioskPoliciesIntro,
  KioskPoliciesSummary,
  KioskPolicyText,
  KioskStageActions,
  KioskStageFormColumn,
  KioskStageSubtitle,
  KioskStageTitle,
  KioskSummaryBox,
  KioskSummaryLabel,
  KioskSummaryRow,
  KioskSummaryValue,
} from './styles';

interface KioskEndRegistrationViewProps {
  employeeId: number;
  employeeName: string;
  pinVerificationToken: string | null;
  onSuccess: (workedLabel: string) => void;
  onEmployeeUnavailable: () => void;
  onAlreadyEnded: () => void;
  onNoActiveRegistration: () => void;
  onPinVerificationExpired: () => void;
  /** Reports submit-in-flight state so the persistent header can disable its close button. */
  onBusyChange: (busy: boolean) => void;
}

interface IEndRegistrationForm {
  date: Date;
  endWork: string; // "HH:mm"
}

const KioskEndRegistrationView = ({
  employeeId,
  employeeName,
  pinVerificationToken,
  onSuccess,
  onEmployeeUnavailable,
  onAlreadyEnded,
  onNoActiveRegistration,
  onPinVerificationExpired,
  onBusyChange,
}: KioskEndRegistrationViewProps) => {
  const [t] = useTranslation();
  const endSession = useEndKioskSession();

  const { data, status, policies } = useKioskClockOutData(employeeId);

  const [breaks, setBreaks] = useState<TKioskBreak[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [polOpen, setPolOpen] = useState(true);

  useEffect(() => {
    onBusyChange(submitting);
  }, [submitting, onBusyChange]);

  const { control, handleSubmit, reset, watch } = useForm<IEndRegistrationForm>({
    defaultValues: { date: new Date(), endWork: '' },
  });

  const date = watch('date');
  const endWork = watch('endWork');

  // 'unavailable' (left the location) and 'no-active' (already ended → clock-in) route out of the view.
  useEffect(() => {
    if (status === 'unavailable') onEmployeeUnavailable();
    if (status === 'no-active') onNoActiveRegistration();
  }, [status, onEmployeeUnavailable, onNoActiveRegistration]);

  // Prefill once data arrives: Date + End work from serverNow; breaks start empty.
  useEffect(() => {
    if (!data) return;
    setSubmitError(null);
    setBreaks([]);
    const serverNow = data.serverNowUtc ? parseUtcTimestamp(data.serverNowUtc) : new Date();
    reset({ date: serverNow, endWork: currentTimeHHmm(serverNow) });
  }, [data, reset]);

  const overlappingIndices = useMemo(() => getOverlappingIndices(breaks), [breaks]);

  const { minDate, maxDate } = useMemo(
    () => kioskDateBounds(data?.serverNowUtc ? parseUtcTimestamp(data.serverNowUtc) : new Date()),
    [data],
  );

  const summary = useMemo(
    () =>
      data
        ? computeWorkSummary({
            startTimeUtc: data.startTime,
            date,
            endWork,
            breaks,
            plannedTotalMinutes: data.plannedTotalMinutes,
          })
        : null,
    [data, date, endWork, breaks],
  );

  const canSubmit =
    !submitting &&
    status === 'ready' &&
    Boolean(data) &&
    isHHmm(endWork) &&
    areBreaksValid(breaks) &&
    overlappingIndices.length === 0 &&
    (data ? breaks.length <= data.maximumNumberOfBreaks : false);

  const onSubmit = async (form: IEndRegistrationForm) => {
    const token = kioskSessionService.getToken();
    if (!token || !data) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = buildKioskClockOutPayload(
        employeeId,
        data.id,
        form.date,
        form.endWork,
        breaks,
        pinVerificationToken,
      );
      const result = await kioskClockOutRequest(token, body);
      onSuccess(formatWorkedDuration(extractWorkedMinutes(result)));
    } catch (e) {
      const httpStatus = (e as { response?: { status?: number } })?.response?.status;
      const code = (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? '';
      if (httpStatus === 401) {
        if (code.includes('PIN_VERIFICATION_REQUIRED')) {
          onPinVerificationExpired();
          return;
        }
        endSession();
        return;
      }
      if (code.includes('EMPLOYEE_NOT_AT_LOCATION')) {
        onEmployeeUnavailable();
        return;
      }
      if (code.includes('REGISTRATION_ALREADY_STOPPED')) {
        onAlreadyEnded();
        return;
      }
      setSubmitError(mapKioskError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <KioskStageTitle>{t(KIOSK__END_REGISTRATION_TITLE)}</KioskStageTitle>
      <KioskStageSubtitle>{employeeName}</KioskStageSubtitle>
      <KioskStageFormColumn>
        {status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <CircularProgress color="inherit" size={28} />
          </Box>
        )}

        {status === 'error' && <KioskErrorMessage>{t(GENERAL__ERROR_MESSAGE)}</KioskErrorMessage>}

        {status === 'ready' && data && (
          <KioskFieldContainer>
            <KioskFieldRow>
              <UPInput
                value={data.costCentreName ?? ''}
                onChange={() => undefined}
                label={t(KIOSK__COST_CENTRE_LABEL)}
                placeholder=""
                disabled
                id="kiosk-end-work-cost-centre"
              />
              <UPInput
                value={formatTime(data.startTime)}
                onChange={() => undefined}
                label={t(KIOSK__START_REGISTRATION_START_LABEL)}
                placeholder=""
                disabled
                id="kiosk-end-work-start"
              />
            </KioskFieldRow>

            <KioskFieldRow>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <UPDatepicker
                    value={field.value}
                    onChange={field.onChange}
                    label={t(KIOSK__START_REGISTRATION_DATE_LABEL)}
                    disabled={submitting}
                    minDate={minDate}
                    maxDate={maxDate}
                    disableInput
                    className="datepicker--white-background"
                  />
                )}
              />
              <Controller
                name="endWork"
                control={control}
                render={({ field }) => (
                  <UPInput
                    value={field.value}
                    onChange={e => field.onChange(formatKioskTimeInput(field.value, e.target.value))}
                    label={t(KIOSK__START_REGISTRATION_END_LABEL)}
                    placeholder="HH:mm"
                    maxLength={5}
                    disabled={submitting}
                    inputClassName="white-background"
                    id="kiosk-end-work-end"
                  />
                )}
              />
            </KioskFieldRow>

            <KioskBreakEditor
              breaks={breaks}
              onChange={setBreaks}
              overlappingIndices={overlappingIndices}
              maxBreaks={data.maximumNumberOfBreaks}
              disabled={submitting}
            />

            {policies.length > 0 && (
              <KioskPoliciesAccordion expanded={polOpen} onChange={() => setPolOpen(v => !v)}>
                <KioskPoliciesSummary expandIcon={<ExpandMoreIcon />}>
                  {t(KIOSK__START_REGISTRATION_POLICIES_TITLE)}
                </KioskPoliciesSummary>
                <KioskPoliciesDetails>
                  <KioskPoliciesIntro>{t(KIOSK__START_REGISTRATION_POLICIES_INTRO)}</KioskPoliciesIntro>
                  {policies.map(p => (
                    <KioskPolicyText key={p.key}>{p.label}</KioskPolicyText>
                  ))}
                </KioskPoliciesDetails>
              </KioskPoliciesAccordion>
            )}

            {summary && (
              <KioskSummaryBox>
                <KioskSummaryRow>
                  <KioskSummaryLabel>{t(KIOSK__END_REGISTRATION_TOTAL_WORKED_LABEL)}</KioskSummaryLabel>
                  <KioskSummaryValue>{formatWorkedDuration(summary.totalWorkedMinutes)}</KioskSummaryValue>
                </KioskSummaryRow>
                {summary.deviationMinutes !== null && (
                  <KioskSummaryRow>
                    <KioskSummaryLabel>{t(KIOSK__END_REGISTRATION_DEVIATION_LABEL)}</KioskSummaryLabel>
                    <KioskSummaryValue>{formatDeviation(summary.deviationMinutes)}</KioskSummaryValue>
                  </KioskSummaryRow>
                )}
              </KioskSummaryBox>
            )}

            {submitError && <KioskErrorMessage>{t(submitError)}</KioskErrorMessage>}
          </KioskFieldContainer>
        )}

        {status === 'ready' && (
          <KioskStageActions>
            <UPButton
              text={t(KIOSK__END_REGISTRATION_BUTTON)}
              onClick={handleSubmit(onSubmit)}
              disabled={!canSubmit}
              id="kiosk-end-work-submit"
            />
          </KioskStageActions>
        )}
      </KioskStageFormColumn>
    </>
  );
};

export default KioskEndRegistrationView;
